import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  type MouseEvent,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@aircall/ds";
import { Check, CircleCheck, Copy } from "@aircall/react-icons";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useCopyToClipboard } from "usehooks-ts";

/** Tick-icon treatment shown during the copied feedback window. */
type CopyToClipboardTextVariant = "default" | "filled";

export interface CopyToClipboardTextProps
  extends Omit<ComponentPropsWithoutRef<"div">, "children" | "onCopy"> {
  /** Test id applied to the displayed text node. */
  "data-test"?: string;
  /** The text rendered and copied to the clipboard. */
  children: string;
  /** Called with the copied text once the copy succeeds. */
  onCopy?: (value: string) => void;
  /** How long (ms) the "copied" feedback state stays visible. */
  timeoutDuration?: number;
  /**
   * When provided, wraps the copy button in a tooltip showing `copy` before a
   * copy and `copied` during the feedback window. Omit to render no tooltip.
   */
  tooltipTexts?: {
    copy: string;
    copied: string;
  };
  /** Tick-icon treatment: outlined `Check` ("default") or filled `CircleCheck` ("filled"). */
  variant?: CopyToClipboardTextVariant;
}

type CopyButtonProps = ComponentPropsWithoutRef<"button"> & {
  copied: boolean;
  variant: CopyToClipboardTextVariant;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  dataTest: string;
};

const CopyButton = forwardRef<HTMLButtonElement, CopyButtonProps>(
  function CopyButton(
    { copied, variant, onClick, dataTest, className, ...rest },
    ref
  ) {
    const TickIcon = variant === "filled" ? CircleCheck : Check;
    const Icon = copied ? TickIcon : Copy;

    const shouldReduceMotion = useReducedMotion();

    // Fade the icon out, then fade the replacement in, over a 200ms ease-out
    // window. Disabled under prefers-reduced-motion.
    const fadeProps = shouldReduceMotion
      ? {}
      : {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
          transition: { duration: 0.2, ease: [0.215, 0.61, 0.355, 1] },
        };

    return (
      <Button
        ref={ref}
        type="button"
        variant="ghost"
        size="icon-sm"
        data-slot="copy-to-clipboard-button"
        data-test={dataTest}
        // Guard against re-copying while the feedback state is shown.
        onClick={copied ? undefined : onClick}
        className={cn(
          copied ? "text-primary" : "text-muted-foreground",
          className
        )}
        {...rest}
      >
        {/* `mode="wait"` swaps copy ↔ tick in the same slot, fading the old
            icon out before the new one in; `initial={false}` skips first mount. */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={copied ? "tick" : "copy"}
            className="flex"
            {...fadeProps}
          >
            <Icon className="size-4" />
          </motion.span>
        </AnimatePresence>
      </Button>
    );
  }
);
CopyButton.displayName = "CopyButton";

export const CopyToClipboardText = forwardRef<
  HTMLDivElement,
  CopyToClipboardText.Props
>(
  (
    componentProps: CopyToClipboardText.Props,
    forwardRef: ForwardedRef<HTMLDivElement>
  ) => {
    const {
      "data-test": dataTest = "copy-to-clipboard-text",
      children,
      className,
      onCopy,
      timeoutDuration = 1000,
      tooltipTexts,
      variant = "default",
      ...rest
    } = componentProps;

    const [, copy] = useCopyToClipboard();
    const [copied, setCopied] = useState(false);
    const timeout = useRef(0);

    const handleClick = useCallback(
      async (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        // Only surface the copied state when the clipboard write actually
        // succeeded. The button disables its onClick while copied, so this never
        // stacks timeouts.
        const succeeded = await copy(children);
        if (!succeeded) {
          return;
        }

        setCopied(true);
        onCopy?.(children);

        timeout.current = window.setTimeout(() => {
          setCopied(false);
        }, timeoutDuration);
      },
      [copy, children, onCopy, timeoutDuration]
    );

    useEffect(() => () => clearTimeout(timeout.current), []);

    const button = (
      <CopyButton
        copied={copied}
        variant={variant}
        onClick={handleClick}
        dataTest={copied ? "copied-icon" : "copy-icon"}
      />
    );

    return (
      <div
        ref={forwardRef}
        data-slot="copy-to-clipboard-text"
        className={cn("flex items-center gap-0.5", className)}
        {...rest}
      >
        <span
          data-slot="copy-to-clipboard-value"
          data-test={dataTest}
          className="text-sm text-muted-foreground"
        >
          {children}
        </span>
        {tooltipTexts ? (
          <Tooltip>
            <TooltipTrigger render={button} />
            <TooltipContent side="top" align="center">
              {copied ? tooltipTexts.copied : tooltipTexts.copy}
            </TooltipContent>
          </Tooltip>
        ) : (
          button
        )}
      </div>
    );
  }
);
CopyToClipboardText.displayName = "CopyToClipboardText";

export namespace CopyToClipboardText {
  export type Props = CopyToClipboardTextProps;
}
